import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { RouteProp, useRoute, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { SVG_COMPONENTS } from '@/assets/admin';
import { IMaintenanceListResponse, IMaintenanceFile } from '@/store/api/maintenanceApi';
import { getTicketStatusInfo } from '@/utils/residents/maintenance';
import { KeyboardSafeView } from '@/components/common/KeyboardSafeView';
import { MaintenanceImageCarousel } from '@/components/residents/maintenance/shared/MaintenanceImageCarousel';
import { WorkRequestInfoSection } from '@/components/residents/maintenance/shared/WorkRequestInfoSection';
import { ChatCTACard } from '@/components/residents/maintenance/shared/ChatCTACard';
import { useFindConversationBySubject } from '@/hooks/residents/useMessages';

type MaintenanceDetailRouteProp = RouteProp<RootStackParamList, 'MaintenanceDetail'>;

const CloseIcon = SVG_COMPONENTS.CANCEL_X;

/**
 * MobileMaintenanceDetailScreen
 * Displays detailed information about a maintenance ticket
 */
export const MobileMaintenanceDetailScreen: React.FC = () => {
  const route = useRoute<MaintenanceDetailRouteProp>();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { ticket } = route.params;
  const { findBySubject, isSearching } = useFindConversationBySubject();
  
  // Get status info using utility
  const statusInfo = getTicketStatusInfo(ticket);
  
  // Get file attachments from ticket
  const images: string[] = ticket.files?.map((f: IMaintenanceFile) => f.fileUrl).filter(Boolean) || [];

  const handleClose = () => {
    navigation.goBack();
  };

  const handleStartChat = async () => {
    const subjectPrefix = `WO-${ticket.maintenanceRequestId}`;
    const subject = `${subjectPrefix}: ${ticket.maintenanceDescription?.substring(0, 50) || 'Maintenance Request'}`;
    
    // Check if a conversation already exists for this maintenance request
    const existingConversationId = await findBySubject(subjectPrefix);
    
    if (existingConversationId) {
      // Navigate to existing conversation
      (navigation as any).navigate('Main', {
        screen: 'ResidentMessages',
        params: {
          screen: 'Conversation',
          params: { conversationId: existingConversationId, subject },
        },
      });
    } else {
      // Navigate to create new conversation with pre-filled subject
      (navigation as any).navigate('Main', {
        screen: 'ResidentMessages',
        params: {
          screen: 'NewConversation',
          params: { initialSubject: subject },
        },
      });
    }
  };

  return (
    <KeyboardSafeView isModal style={styles.container}>
      <View style={styles.innerContainer}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Details</Text>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <CloseIcon width={16} height={16} color={MARKETPLACE_COLORS.textMuted} />
          </TouchableOpacity>
        </View>

        <ScrollView
          style={styles.content}
          contentContainerStyle={[
            styles.contentContainer,
            { paddingBottom: Math.max(insets.bottom, 24) },
          ]}
          showsVerticalScrollIndicator={false}
        >
          {/* Status Section */}
          <View style={styles.section}>
            <View style={styles.statusRow}>
              <Text style={styles.sectionTitle}>Status</Text>
              <Text style={styles.statusLabel}>{statusInfo.label}</Text>
            </View>
            <View style={styles.progressContainer}>
              <View style={[styles.progressSegment, statusInfo.progress >= 1 && styles.progressSegmentActive]} />
              <View style={[styles.progressSegment, statusInfo.progress >= 2 && styles.progressSegmentActive]} />
              <View style={[styles.progressSegment, statusInfo.progress >= 3 && styles.progressSegmentActive]} />
            </View>
          </View>

          {/* Problem Section */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Problem</Text>
            
            {/* Image Carousel using shared component */}
            <MaintenanceImageCarousel images={images} variant="mobile" />

            {/* Problem Description */}
            <View style={styles.problemDescription}>
              <Text style={styles.problemTitle}>
                {ticket.maintenanceDescription?.substring(0, 50) || `Request #${ticket.maintenanceRequestId}`}
              </Text>
              {ticket.maintenanceDescription && (
                <Text style={styles.problemText}>
                  {ticket.maintenanceDescription}
                </Text>
              )}
            </View>
          </View>

          {/* Work Request Information using shared component */}
          <WorkRequestInfoSection ticket={ticket} variant="mobile" />

          {/* Chat CTA Card using shared component */}
          <ChatCTACard onPress={handleStartChat} variant="mobile" loading={isSearching} />
        </ScrollView>
      </View>
    </KeyboardSafeView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: MARKETPLACE_COLORS.modalBackground,
  },
  innerContainer: {
    flex: 1,
  },
  
  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.dividerGray,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 27,
  },
  closeButton: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  // Content
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 24,
  },
  
  // Sections
  section: {
    gap: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  
  // Status
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusLabel: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 24,
  },
  progressContainer: {
    flexDirection: 'row',
    gap: 6,
  },
  progressSegment: {
    flex: 1,
    height: 8,
    borderRadius: 8,
    backgroundColor: MARKETPLACE_COLORS.white10Percent,
  },
  progressSegmentActive: {
    backgroundColor: '#66CE66',
  },
  
  // Problem
  problemDescription: {
    gap: 4,
  },
  problemTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  problemText: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 21,
  },
});

export default MobileMaintenanceDetailScreen;
